import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { Button, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRotate, faFileArrowDown } from "@fortawesome/free-solid-svg-icons";

import { DarkModeContext } from "../../context/DarkModeContext";
import logo from "../../assets/img/logo.png";

import BienvenidoAdmin from "../../components/administracion/BienvenidoAdmin";
import TituloPagina from "../../components/comunes/TituloPagina";
import Footer from "../../components/comunes/Footer";
import TablaCRMSeguimiento from "../../components/administracion/TablaCRMSeguimiento";
import "./AdminCRMSeguimiento.css";
import { getToken } from "../../utils/auth";


const AdminCRMSeguimiento = () => {
    const apiUrlToken = process.env.REACT_APP_API_TOKEN;

    const { darkMode } = useContext(DarkModeContext);
    const navegacion = useNavigate();

    const [datos, setDatos] = useState([]);
    const [loadingTabla, setLoadingTabla] = useState(true);
    const [cargandoToken, setCargandoToken] = useState(true);
    const [descargandoExcel, setDescargandoExcel] = useState(false);
    const [reglasPostventa, setReglasPostventa] = useState([]);

    const swalModalClasses = useMemo(
        () => ({
            container: darkMode ? "swal2-container--dark--popup" : "swal2-container--light--popup",
            popup: "crm-postventa-swal-popup",
            title: "crm-postventa-swal-title",
            htmlContainer: "crm-postventa-swal-html",
            confirmButton: "crm-postventa-swal-btn crm-postventa-swal-btn-confirm",
            cancelButton: "crm-postventa-swal-btn crm-postventa-swal-btn-cancel",
        }),
        [darkMode]
    );

    const escapeHtml = (valor) =>
        String(valor ?? "")
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#39;");

    const aFechaInputLocal = (fecha) => {
        if (!fecha) return "";
        const date = new Date(fecha);
        if (Number.isNaN(date.getTime())) return "";
        const tzOffset = date.getTimezoneOffset() * 60000;
        return new Date(date.getTime() - tzOffset).toISOString().slice(0, 16);
    };

    const redireccionar = useCallback(() => {
        navegacion("/");
    }, [navegacion]);

    const manejarRespuestaNoAutorizada = useCallback(() => {
        sessionStorage.removeItem("token");

        Swal.fire({
            title: "Sesion expirada o token invalido.",
            text: "Inicie sesion nuevamente.",
            imageUrl: logo,
            imageWidth: 100,
            imageHeight: 30,
            imageAlt: "Logo",
            confirmButtonText: "Ok",
            timer: 5000,
            allowOutsideClick: true,
            customClass: {
                container: darkMode ? "swal2-container--dark" : "swal2-container--light",
                confirmButton: "my-swal-button",
            },
        }).then(redireccionar);
    }, [darkMode, redireccionar]);

    const manejarAccesoDenegado = useCallback(() => {
        Swal.fire({
            title: "Acceso denegado.",
            text: "No tiene permisos para este panel.",
            imageUrl: logo,
            imageWidth: 100,
            imageHeight: 30,
            imageAlt: "Logo",
            confirmButtonText: "Ok",
            timer: 5000,
            allowOutsideClick: true,
            customClass: {
                container: darkMode ? "swal2-container--dark" : "swal2-container--light",
                confirmButton: "my-swal-button",
            },
        }).then(redireccionar);
    }, [darkMode, redireccionar]);

    const cargarPostventa = useCallback(async (tokenParam) => {
        const token = tokenParam || getToken();
        if (!token) return;

        setLoadingTabla(true);
        try {
            const response = await fetch(process.env.REACT_APP_API_CRM_POSTVENTA_LISTAR, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ Token: token }),
            });

            if (response.status === 401 || response.status === 403) {
                manejarRespuestaNoAutorizada();
                return;
            }

            if (!response.ok) {
                throw new Error(`Error ${response.status}`);
            }

            const data = await response.json();
            setDatos(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error("Error al listar postventa:", error);
            setDatos([]);
            Swal.fire("Error", "No se pudo cargar el listado de postventa.", "error");
        } finally {
            setLoadingTabla(false);
        }
    }, [manejarRespuestaNoAutorizada]);

    const cargarReglasPostventa = useCallback(async () => {
        if (Array.isArray(reglasPostventa) && reglasPostventa.length > 0) {
            return reglasPostventa;
        }

        const token = getToken();
        if (!token) {
            manejarRespuestaNoAutorizada();
            return [];
        }

        try {
            const response = await fetch(process.env.REACT_APP_API_CRM_POSTVENTA_REGLAS, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ Token: token }),
            });

            if (response.status === 401 || response.status === 403) {
                manejarRespuestaNoAutorizada();
                return [];
            }

            if (!response.ok) {
                throw new Error(`Error ${response.status}`);
            }

            const data = await response.json();
            const lista = Array.isArray(data) ? data : [];
            setReglasPostventa(lista);
            return lista;
        } catch (error) {
            console.error("Error cargando reglas postventa:", error);
            Swal.fire("Error", "No se pudieron cargar las acciones de postventa.", "error");
            return [];
        }
    }, [manejarRespuestaNoAutorizada, reglasPostventa]);

    const handleVerHistorial = useCallback(async (item) => {
        const token = getToken();
        if (!token) {
            manejarRespuestaNoAutorizada();
            return;
        }

        try {
            const response = await fetch(process.env.REACT_APP_API_CRM_OBTENER_HISTORIAL, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    Token: token,
                    Cuit: item?.cuit || null,
                    Telefono: item?.telefono || null,
                }),
            });

            if (response.status === 401 || response.status === 403) {
                manejarRespuestaNoAutorizada();
                return;
            }

            if (!response.ok) {
                throw new Error(`Error ${response.status}`);
            }

            const historial = await response.json();
            const lista = Array.isArray(historial) ? historial : [];

            if (lista.length === 0) {
                Swal.fire("Historial", "No hay historial para este registro.", "info");
                return;
            }

            const filas = lista
                .slice(0, 40)
                .map((h) => {
                    const fecha = h?.fechaGestion
                        ? new Date(h.fechaGestion).toLocaleString("es-AR")
                        : "--";
                    const accion = h?.reglaAplicada?.aliasVista || h?.estadoResultante || "-";
                    const observacion = h?.observacion || "-";
                    return `
                        <tr>
                            <td>${escapeHtml(fecha)}</td>
                            <td>${escapeHtml(accion)}</td>
                            <td class="is-break">${escapeHtml(observacion)}</td>
                        </tr>`;
                })
                .join("");

            const html = `
                <div class="crm-postventa-table-wrap">
                    <table class="crm-postventa-table">
                        <thead>
                            <tr>
                                <th>Fecha</th>
                                <th>Accion</th>
                                <th>Observacion</th>
                            </tr>
                        </thead>
                        <tbody>${filas}</tbody>
                    </table>
                </div>`;

            Swal.fire({
                title: "Historial CRM",
                html,
                width: 900,
                confirmButtonText: "Cerrar",
                buttonsStyling: false,
                customClass: swalModalClasses,
            });
        } catch (error) {
            console.error("Error al obtener historial:", error);
            Swal.fire("Error", "No se pudo cargar el historial.", "error");
        }
    }, [manejarRespuestaNoAutorizada, swalModalClasses]);

    const handleAgregarSeguimiento = useCallback(async (item) => {
        const token = getToken();
        if (!token) {
            manejarRespuestaNoAutorizada();
            return;
        }

        const reglas = await cargarReglasPostventa();
        if (!reglas.length) return;

        const optionsHtml = reglas
            .map((r) => `<option value="${r.id}">${escapeHtml(r.aliasVista || r.nombreAccion || `Regla ${r.id}`)}</option>`)
            .join("");

        const result = await Swal.fire({
            title: "Agregar seguimiento",
            html: `
                <div class="crm-postventa-form">
                    <label class="crm-postventa-label" for="crm-postventa-regla">Accion</label>
                    <select id="crm-postventa-regla" class="crm-postventa-input">${optionsHtml}</select>
                    <label class="crm-postventa-label" for="crm-postventa-observacion">Observacion</label>
                    <textarea id="crm-postventa-observacion" class="crm-postventa-textarea" placeholder="Detalle de la gestion"></textarea>
                </div>
            `,
            showCancelButton: true,
            confirmButtonText: "Guardar",
            cancelButtonText: "Cancelar",
            buttonsStyling: false,
            customClass: swalModalClasses,
            focusConfirm: false,
            preConfirm: () => {
                const idRegla = Number(document.getElementById("crm-postventa-regla")?.value || 0);
                const observacion = (document.getElementById("crm-postventa-observacion")?.value || "").trim();

                if (!idRegla) {
                    Swal.showValidationMessage("Selecciona una accion.");
                    return false;
                }
                if (!observacion) {
                    Swal.showValidationMessage("Ingresa una observacion.");
                    return false;
                }

                return { idRegla, observacion };
            },
        });

        if (!result.isConfirmed || !result.value) return;

        try {
            const response = await fetch(process.env.REACT_APP_API_CRM_REGISTRAR_GESTION, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    Token: token,
                    IdSeguimientoAnterior: item.id,
                    IdReglaNueva: result.value.idRegla,
                    Observacion: result.value.observacion,
                    IdAlta: item?.idAlta ?? null,
                }),
            });

            if (response.status === 401 || response.status === 403) {
                manejarRespuestaNoAutorizada();
                return;
            }

            if (!response.ok) {
                let mensaje = "No se pudo registrar el seguimiento.";
                try {
                    const data = await response.json();
                    mensaje = data?.mensaje || mensaje;
                } catch { }
                throw new Error(mensaje);
            }

            Swal.fire("Ok", "Seguimiento agregado.", "success");
            await cargarPostventa(token);
        } catch (error) {
            console.error("Error al registrar seguimiento:", error);
            Swal.fire("Error", error.message || "No se pudo registrar el seguimiento.", "error");
        }
    }, [cargarPostventa, cargarReglasPostventa, manejarRespuestaNoAutorizada, swalModalClasses]);

    const handleEditarSeguimiento = useCallback(async (item) => {
        const token = getToken();
        if (!token) {
            manejarRespuestaNoAutorizada();
            return;
        }

        const fechaInicial = aFechaInputLocal(item?.fechaGestion) || aFechaInputLocal(new Date());

        const result = await Swal.fire({
            title: "Editar gestion",
            html: `
                <div class="crm-postventa-form">
                    <label class="crm-postventa-label" for="crm-editar-fecha">Fecha de gestion</label>
                    <input id="crm-editar-fecha" type="datetime-local" class="crm-postventa-input" value="${escapeHtml(fechaInicial)}" />
                    <label class="crm-postventa-label" for="crm-editar-observacion">Observacion</label>
                    <textarea id="crm-editar-observacion" class="crm-postventa-textarea" placeholder="Detalle de la gestion">${escapeHtml(item?.observacion || "")}</textarea>
                </div>
            `,
            showCancelButton: true,
            confirmButtonText: "Guardar",
            cancelButtonText: "Cancelar",
            buttonsStyling: false,
            customClass: swalModalClasses,
            focusConfirm: false,
            preConfirm: () => {
                const fechaGestion = document.getElementById("crm-editar-fecha")?.value;
                const observacion = (document.getElementById("crm-editar-observacion")?.value || "").trim();

                if (!fechaGestion) {
                    Swal.showValidationMessage("Ingresa fecha de gestion.");
                    return false;
                }
                if (!observacion) {
                    Swal.showValidationMessage("Ingresa observacion.");
                    return false;
                }

                return { fechaGestion, observacion };
            },
        });

        if (!result.isConfirmed || !result.value) return;

        try {
            const response = await fetch(process.env.REACT_APP_API_CRM_EDITAR_SEGUIMIENTO, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    Token: token,
                    IdSeguimiento: item.id,
                    FechaGestion: new Date(result.value.fechaGestion).toISOString(),
                    Observacion: result.value.observacion,
                }),
            });

            if (response.status === 401 || response.status === 403) {
                manejarRespuestaNoAutorizada();
                return;
            }

            if (!response.ok) {
                let mensaje = "No se pudo editar el seguimiento.";
                try {
                    const data = await response.json();
                    mensaje = data?.mensaje || mensaje;
                } catch { }
                throw new Error(mensaje);
            }

            Swal.fire("Ok", "Seguimiento actualizado.", "success");
            await cargarPostventa(token);
        } catch (error) {
            console.error("Error al editar seguimiento:", error);
            Swal.fire("Error", error.message || "No se pudo editar el seguimiento.", "error");
        }
    }, [cargarPostventa, manejarRespuestaNoAutorizada, swalModalClasses]);

    const handleAsignarAlta = useCallback(async (item) => {
        const token = getToken();
        if (!token) {
            manejarRespuestaNoAutorizada();
            return;
        }

        if (!item?.cuit) {
            Swal.fire("Atencion", "Este registro no tiene CUIT para buscar altas disponibles.", "warning");
            return;
        }

        try {
            const responseAltas = await fetch(process.env.REACT_APP_API_CRM_POSTVENTA_ALTAS, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    Token: token,
                    Cuit: item.cuit,
                }),
            });

            if (responseAltas.status === 401 || responseAltas.status === 403) {
                manejarRespuestaNoAutorizada();
                return;
            }

            if (!responseAltas.ok) {
                throw new Error(`Error ${responseAltas.status}`);
            }

            const altas = await responseAltas.json();
            const listaAltas = Array.isArray(altas) ? altas : [];

            if (!listaAltas.length) {
                Swal.fire("Sin resultados", "No hay terminales en estado ALTA para este CUIT.", "info");
                return;
            }

            const optionsHtml = listaAltas
                .map((a) => {
                    const label = `${a.id} - T:${a.terminal ?? "-"} - C:${a.comercio ?? "-"} - ${a.nombreFantasia ?? ""}`;
                    return `<option value="${a.id}">${escapeHtml(label)}</option>`;
                })
                .join("");

            const result = await Swal.fire({
                title: "Asignar Alta ID",
                html: `
                    <div class="crm-postventa-form">
                        <label class="crm-postventa-label" for="crm-asignar-alta">Terminal disponible</label>
                        <select id="crm-asignar-alta" class="crm-postventa-input">${optionsHtml}</select>
                        <label class="crm-postventa-label" for="crm-asignar-observacion">Observacion (opcional)</label>
                        <textarea id="crm-asignar-observacion" class="crm-postventa-textarea" placeholder="Comentario interno"></textarea>
                    </div>
                `,
                showCancelButton: true,
                confirmButtonText: "Asignar",
                cancelButtonText: "Cancelar",
                buttonsStyling: false,
                customClass: swalModalClasses,
                focusConfirm: false,
                preConfirm: () => {
                    const idAlta = Number(document.getElementById("crm-asignar-alta")?.value || 0);
                    const observacion = (document.getElementById("crm-asignar-observacion")?.value || "").trim();

                    if (!idAlta) {
                        Swal.showValidationMessage("Selecciona un Alta ID.");
                        return false;
                    }

                    return { idAlta, observacion };
                },
            });

            if (!result.isConfirmed || !result.value) return;

            const responseAsignar = await fetch(process.env.REACT_APP_API_CRM_POSTVENTA_ASIGNAR_ALTA, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    Token: token,
                    IdSeguimiento: item.id,
                    IdAlta: result.value.idAlta,
                    Observacion: result.value.observacion || null,
                }),
            });

            if (responseAsignar.status === 401 || responseAsignar.status === 403) {
                manejarRespuestaNoAutorizada();
                return;
            }

            if (!responseAsignar.ok) {
                let mensaje = "No se pudo asignar el Alta ID.";
                try {
                    const data = await responseAsignar.json();
                    mensaje = data?.mensaje || mensaje;
                } catch { }
                throw new Error(mensaje);
            }

            Swal.fire("Ok", "Alta ID asignado.", "success");
            await cargarPostventa(token);
        } catch (error) {
            console.error("Error al asignar Alta ID:", error);
            Swal.fire("Error", error.message || "No se pudo asignar el Alta ID.", "error");
        }
    }, [cargarPostventa, manejarRespuestaNoAutorizada, swalModalClasses]);

    const handleVerFacturacion = useCallback(async (item) => {
        const token = getToken();
        if (!token) {
            manejarRespuestaNoAutorizada();
            return;
        }

        const idAlta = Number(item?.idAlta || 0);
        if (!idAlta) {
            Swal.fire("Atencion", "Primero debes asignar un Alta ID a este caso.", "warning");
            return;
        }

        try {
            const response = await fetch(process.env.REACT_APP_API_CRM_POSTVENTA_FACTURACION, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    Token: token,
                    IdAlta: idAlta,
                    Dias: 16,
                }),
            });

            if (response.status === 401 || response.status === 403) {
                manejarRespuestaNoAutorizada();
                return;
            }

            if (!response.ok) {
                let mensaje = "No se pudo obtener la facturacion.";
                try {
                    const data = await response.json();
                    mensaje = data?.mensaje || mensaje;
                } catch { }
                throw new Error(mensaje);
            }

            const data = await response.json();
            const diario = Array.isArray(data?.diario) ? data.diario : [];
            const movimientos = Array.isArray(data?.movimientos) ? data.movimientos : [];

            const filasDiarias = diario.length
                ? diario
                    .map((d) => `
                        <tr>
                            <td>${escapeHtml(d.fecha)}</td>
                            <td class="is-right">$ ${Number(d.totalBruto || 0).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            <td class="is-right">${escapeHtml(d.cantidadOperaciones)}</td>
                        </tr>
                    `)
                    .join("")
                : `<tr><td colspan="3" class="is-center">Sin movimientos</td></tr>`;

            const filasMovimientos = movimientos.length
                ? movimientos
                    .slice(0, 40)
                    .map((m) => `
                        <tr>
                            <td>${escapeHtml(m.fechaPago || "--")}</td>
                            <td>${escapeHtml(m.tarjeta || "-")}</td>
                            <td>${escapeHtml(m.tipo || "-")}</td>
                            <td class="is-right">$ ${Number(m.totalBruto || 0).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        </tr>
                    `)
                    .join("")
                : `<tr><td colspan="4" class="is-center">Sin movimientos</td></tr>`;

            const html = `
                <div class="crm-postventa-facturacion">
                    <div class="crm-postventa-summary">
                        <b>Alta ID:</b> ${escapeHtml(data?.idAlta)} |
                        <b>Terminal:</b> ${escapeHtml(data?.terminal ?? "-")} |
                        <b>Comercio:</b> ${escapeHtml(data?.comercio ?? "-")}<br/>
                        <b>Periodo:</b> ${escapeHtml(data?.fechaInicio)} - ${escapeHtml(data?.fechaFin)} (${escapeHtml(data?.dias)} dias)<br/>
                        <b>Total Bruto:</b> $ ${Number(data?.totalBruto || 0).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} |
                        <b>Total Neto:</b> $ ${Number(data?.totalNeto || 0).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} |
                        <b>Ops:</b> ${escapeHtml(data?.cantidadOperaciones)}
                    </div>

                    <div class="crm-postventa-grid">
                        <div class="crm-postventa-table-wrap is-short">
                            <table class="crm-postventa-table">
                                <thead>
                                    <tr>
                                        <th>Fecha</th>
                                        <th class="is-right">Total Bruto</th>
                                        <th class="is-right">Ops</th>
                                    </tr>
                                </thead>
                                <tbody>${filasDiarias}</tbody>
                            </table>
                        </div>

                        <div class="crm-postventa-table-wrap is-large">
                            <table class="crm-postventa-table">
                                <thead>
                                    <tr>
                                        <th>Fecha Pago</th>
                                        <th>Tarjeta</th>
                                        <th>Tipo</th>
                                        <th class="is-right">Bruto</th>
                                    </tr>
                                </thead>
                                <tbody>${filasMovimientos}</tbody>
                            </table>
                        </div>
                    </div>
                </div>
            `;

            Swal.fire({
                title: "Facturacion",
                html,
                width: 1080,
                confirmButtonText: "Cerrar",
                buttonsStyling: false,
                customClass: swalModalClasses,
            });
        } catch (error) {
            console.error("Error al ver facturacion:", error);
            Swal.fire("Error", error.message || "No se pudo obtener la facturacion.", "error");
        }
    }, [manejarRespuestaNoAutorizada, swalModalClasses]);

    const descargarExcelBase = useCallback(async () => {
        const token = getToken();
        if (!token) {
            manejarRespuestaNoAutorizada();
            return;
        }

        setDescargandoExcel(true);
        try {
            const response = await fetch(process.env.REACT_APP_API_CRM_EXPORTAR_EXCEL, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ Token: token }),
            });

            if (response.status === 401 || response.status === 403) {
                manejarRespuestaNoAutorizada();
                return;
            }

            if (!response.ok) {
                let mensaje = "No se pudo generar el Excel.";
                try {
                    const data = await response.json();
                    mensaje = data?.mensaje || mensaje;
                } catch { }
                throw new Error(mensaje);
            }

            const blob = await response.blob();
            const disposition = response.headers.get("content-disposition") || "";
            let fileName = "CRM_Base.xlsx";
            const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i);
            if (match?.[1]) {
                fileName = match[1].replace(/['"]/g, "").trim();
            }

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Error descargando Excel CRM:", error);
            Swal.fire("Error", error.message || "No se pudo descargar el Excel.", "error");
        } finally {
            setDescargandoExcel(false);
        }
    }, [manejarRespuestaNoAutorizada]);

    useEffect(() => {
        let activo = true;

        const verificarToken = async () => {
            const token = getToken();

            if (!token) {
                manejarRespuestaNoAutorizada();
                return;
            }

            try {
                const response = await fetch(apiUrlToken, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ Token: token }),
                });

                if (response.status === 401 || response.status === 403) {
                    manejarRespuestaNoAutorizada();
                    return;
                }

                if (!response.ok) {
                    throw new Error(`Error token ${response.status}`);
                }

                const raw = await response.text();
                let rol = Number(raw);
                if (!Number.isFinite(rol)) {
                    try {
                        rol = Number(JSON.parse(raw));
                    } catch {
                        rol = NaN;
                    }
                }

                if (!Number.isFinite(rol)) {
                    throw new Error("Respuesta de rol invalida");
                }

                if (rol !== 8) {
                    manejarAccesoDenegado();
                    return;
                }

                if (!activo) return;
                await cargarPostventa(token);
            } catch (error) {
                console.error("Error validando token:", error);
                if (activo) {
                    Swal.fire("Error", "No se pudo validar la sesion en este momento.", "error");
                }
            } finally {
                if (activo) setCargandoToken(false);
            }
        };

        verificarToken();

        return () => {
            activo = false;
        };
    }, [apiUrlToken, manejarRespuestaNoAutorizada, manejarAccesoDenegado, cargarPostventa]);

    if (cargandoToken) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "50vh" }}>
                <Spinner animation="border" />
            </div>
        );
    }

    return (
        <div className="d-flex flex-column min-vh-100" translate="no">
            <div className="pt-5">
                <BienvenidoAdmin />
            </div>

            <div>
                <TituloPagina title="CRM Seguimiento - Postventa" />
            </div>

            <div className="container py-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className={darkMode ? "text-white m-0" : "text-dark m-0"}>
                        Listado Postventa ({datos.length})
                    </h5>

                    <div className="crm-postventa-top-actions">
                        <Button
                            className={darkMode ? "d-flex btn-check-modal-dark centrado border-0 fs-14 lato-bold crm-postventa-main-btn is-excel" : "d-flex btn-check-modal centrado border-0 fs-14 lato-bold crm-postventa-main-btn is-excel"}
                            onClick={descargarExcelBase}
                            disabled={descargandoExcel}
                        >
                            {descargandoExcel ? (
                                <Spinner animation="border" size="sm" />
                            ) : (
                                <>
                                    <FontAwesomeIcon className="me-2" icon={faFileArrowDown} />
                                    Excel Base
                                </>
                            )}
                        </Button>

                        <Button
                            className={darkMode ? "d-flex btn-check-modal-dark centrado border-0 fs-14 lato-bold crm-postventa-main-btn is-refresh" : "d-flex btn-check-modal centrado border-0 fs-14 lato-bold crm-postventa-main-btn is-refresh"}
                            onClick={() => cargarPostventa()}
                            disabled={loadingTabla}
                        >
                            <FontAwesomeIcon className="me-2" icon={faRotate} />
                            Actualizar
                        </Button>
                    </div>
                </div>

                <TablaCRMSeguimiento
                    datos={datos}
                    loading={loadingTabla}
                    darkMode={darkMode}
                    onVerHistorial={handleVerHistorial}
                    onAgregarSeguimiento={handleAgregarSeguimiento}
                    onEditarSeguimiento={handleEditarSeguimiento}
                    onAsignarAlta={handleAsignarAlta}
                    onVerFacturacion={handleVerFacturacion}
                />
            </div>

            <div className="py-4 mt-auto">
                <Footer />
            </div>
        </div>
    );
};

export default AdminCRMSeguimiento;

